import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Stack,
	TextField,
	Typography,
	Box,
	Divider,
} from "@mui/material";
import { useState, useEffect } from "react";
import { MAX_SPLIT_PARTS } from "@/configConstants";

type PaymentInfo = {
	date: string;
	from: string;
	email?: string;
	phone?: string;
	note?: string;
};

type RelevantEvent = {
	id: number;
	date: string;
	costPerParticipant: number;
};

type SplitPaymentDialogProps = {
	open: boolean;
	onClose: () => void;
	onConfirm: (amounts: number[]) => void;
	totalAmount: number; // cents
	paymentInfo?: PaymentInfo;
	relevantEvents?: RelevantEvent[];
};

export default function SplitPaymentDialog({
	open,
	onClose,
	onConfirm,
	totalAmount,
	paymentInfo,
	relevantEvents,
}: SplitPaymentDialogProps) {
	const [splitCount, setSplitCount] = useState<number>(2);
	const [amountsCents, setAmountsCents] = useState<number[]>([Math.floor(totalAmount / 2), Math.ceil(totalAmount / 2)]);

	useEffect(() => {
		if (open) {
			setSplitCount(2);
			setAmountsCents([Math.floor(totalAmount / 2), Math.ceil(totalAmount / 2)]);
		}
	}, [open, totalAmount]);

	const handleSplitCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = Number.parseInt(e.target.value) || 2;
		const count = Math.max(2, Math.min(MAX_SPLIT_PARTS, value));
		setSplitCount(count);
		// Even split in cents
		const base = Math.floor(totalAmount / count);
		const arr = new Array(count).fill(base);
		let rem = totalAmount - base * count;
		let idx = 0;
		while (rem > 0) {
			arr[idx % count] += 1;
			rem -= 1;
			idx += 1;
		}
		setAmountsCents(arr);
	};

	const handleAmountChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
		// Accept decimal like "12.34" or "12,34"
		const normalized = e.target.value.replace(/,/g, ".").trim();
		const num = Number.parseFloat(normalized);
		const cents = Number.isNaN(num) ? 0 : Math.round(num * 100);
		setAmountsCents((prev) => prev.map((v, i) => (i === index ? cents : v)));
	};

	const remainder = totalAmount - amountsCents.slice(0, -1).reduce((s, v) => s + v, 0);
	const isValid = remainder >= 0;

	const handleConfirm = () => {
		if (!isValid) return;
		const final = [...amountsCents.slice(0, -1), remainder];
		onConfirm(final);
		onClose();
	};
	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>Zahlung aufteilen</DialogTitle>
			<DialogContent>
				<Stack spacing={3} sx={{ mt: 1 }}>
					<DialogContentText>
						Gesamtbetrag: <strong>€{(totalAmount / 100).toFixed(2)}</strong>
					</DialogContentText>

					{paymentInfo && (
						<Box>
							<Typography variant="subtitle2" gutterBottom>
								Zahlungsinformationen
							</Typography>
							<Stack spacing={0.5}>
								<Typography variant="body2">
									<strong>Datum:</strong> {paymentInfo.date}
								</Typography>
								<Typography variant="body2">
									<strong>Von:</strong> {paymentInfo.from}
								</Typography>
								{paymentInfo.email && (
									<Typography variant="body2">
										<strong>E-Mail:</strong> {paymentInfo.email}
									</Typography>
								)}
								{paymentInfo.phone && (
									<Typography variant="body2">
										<strong>Telefon:</strong> {paymentInfo.phone}
									</Typography>
								)}
								{paymentInfo.note && (
									<Typography variant="body2">
										<strong>Hinweis:</strong> {paymentInfo.note}
									</Typography>
								)}
							</Stack>
						</Box>
					)}

					{relevantEvents && relevantEvents.length > 0 && (
						<Box>
							<Typography variant="subtitle2" gutterBottom>
								Relevante Veranstaltungen (am oder vor dem Zahlungsdatum)
							</Typography>
							<Stack spacing={0.5}>
								{relevantEvents.map((event) => (
									<Typography key={event.id} variant="body2">
										{event.date} - €{(event.costPerParticipant / 100).toFixed(2)}
									</Typography>
								))}
							</Stack>
						</Box>
					)}

					<Divider />

					<TextField
						label="In wie viele Teile aufteilen?"
						type="number"
						value={splitCount}
						onChange={handleSplitCountChange}
						inputProps={{ min: 2, max: MAX_SPLIT_PARTS }}
						fullWidth
					/>

					<Stack spacing={2}>
						<Typography variant="body2" color="text.secondary">
							Gib Beträge für jeden Teil ein (der letzte Teil wird automatisch berechnet):
						</Typography>

						{amountsCents.map((amount, index) => {
							const isLast = index === amountsCents.length - 1;
							
							if (isLast) {
								return (
									<TextField
										key={index}
										label={`Teil ${index + 1} (Rest)`}
										value={`€${(remainder / 100).toFixed(2)}`}
										disabled
										fullWidth
										error={!isValid}
										helperText={!isValid ? "Restbetrag darf nicht negativ sein" : ""}
									/>
								);
							}

							return (
								<TextField
									key={index}
									label={`Teil ${index + 1} (€)`}
									value={(amount / 100).toFixed(2)}
									onChange={handleAmountChange(index)}
									type="text"
									placeholder="0.00"
									fullWidth
								/>
							);
						})}
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Abbrechen</Button>
				<Button onClick={handleConfirm} variant="contained" disabled={!isValid}>
					Aufteilen
				</Button>
			</DialogActions>
		</Dialog>
	);
}
